import React, { useEffect, useState, useCallback } from 'react'
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout'
import { Helmet } from 'react-helmet/es/Helmet'
import PageTitle from '../../components/PageTitle'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import {
  message,
  Typography,
  Button,
  Space,
  DatePicker,
  Input,
  Divider,
  Row,
  Col,
} from 'antd'
import { inject, observer } from 'mobx-react'
import { useParams, useHistory } from 'react-router-dom'
import moment from 'moment'
import { systemName } from '../../config'
import { UploadOutlined, SaveOutlined } from '@ant-design/icons'
import SalaryVpDetailModal from '../MySalaryVpPage/SalaryVpDetailModal'
import TableComponent from '../../components/Common/TableComponent'

const { Text } = Typography

const SalaryVpPage = props => {
  const { salaryVpStore, loadingAnimationStore } = props
  const { id: salaryCode } = useParams()
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState('')
  const [salaryMonth, setSalaryMonth] = useState(moment())
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const formatCurrencyCell = val => {
    if (val === null || val === undefined || val === '') return '-'
    const numericValue =
      typeof val === 'number'
        ? val
        : Number(String(val).replace(/[^0-9,.-]/g, '').replace(/,/g, ''))
    if (!Number.isFinite(numericValue)) return '-'
    return new Intl.NumberFormat().format(numericValue)
  }

  const fetchData = useCallback(async () => {
    if (!salaryCode) return
    setLoading(true)
    try {
      await salaryVpStore.getSalaryVpDetail(salaryCode)
      setTitle(salaryVpStore.salaryVpInfo.title)
      setSalaryMonth(moment(salaryVpStore.salaryVpInfo.salary_month))
    } catch (err) {
      message.error('Không tìm thấy thông tin bảng lương')
      history.push('/salary')
    } finally {
      setLoading(false)
    }
  }, [salaryCode, salaryVpStore, history])

  useEffect(() => {
    fetchData()
    return () => salaryVpStore.clearSalaryVpDetail()
  }, [fetchData])

  const handleImport = async file => {
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)

    loadingAnimationStore.showSpinner(true)
    try {
      const { data } = await salaryVpStore.importSalaryVpFile(formData)
      message.success('Import dữ liệu thành công')
      if (!title) {
        setTitle(
          `Bảng thanh toán lương nhân viên tháng ${salaryMonth.format(
            'MM/YYYY'
          )}`
        )
      }
    } catch (err) {
      message.error('Lỗi khi import file Excel')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
    return false // Prevent default upload
  }

  const handleCreate = async () => {
    if (!salaryVpStore.salaryVpInfo.from_file_code) {
      return message.warning('Vui lòng import file dữ liệu trước')
    }

    const payload = {
      title: title,
      salary_month: salaryMonth.toISOString(),
      from_file_code: salaryVpStore.salaryVpInfo.from_file_code,
      file_id: salaryVpStore.salaryVpInfo.file_id,
    }

    loadingAnimationStore.showSpinner(true)
    try {
      await salaryVpStore.createSalaryVp(payload)
      message.success('Đã lưu bảng lương thành công')
      history.push('/salary')
    } catch (err) {
      message.error('Lỗi khi tạo trình duyệt lương')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const columns = [
    { title: 'Mã NV', dataIndex: 'ma_nv', key: 'ma_nv', width: 100 },
    {
      title: 'Họ và tên',
      dataIndex: 'ho_va_ten',
      key: 'ho_va_ten',
      width: 200,
    },
    {
      title: 'Chức danh',
      dataIndex: 'chuc_danh_cong_viec',
      key: 'chuc_danh_cong_viec',
      ellipsis: true,
    },
    {
      title: 'Ngày công',
      dataIndex: 'ngay_cong',
      key: 'ngay_cong',
      align: 'center',
      width: 100,
    },
    {
      title: 'Tổng thu nhập',
      dataIndex: 'tong_thu_nhap_tien_luong',
      key: 'tong_thu_nhap_tien_luong',
      align: 'right',
      render: val => formatCurrencyCell(val),
    },
    {
      title: 'Tổng khấu trừ',
      dataIndex: 'tong_khau_tru',
      key: 'tong_khau_tru',
      align: 'right',
      render: val => formatCurrencyCell(val),
    },
    {
      title: 'Thu nhập tiền lương thực lĩnh',
      dataIndex: 'thu_nhap_thuc_linh',
      key: 'thu_nhap_thuc_linh',
      align: 'right',
      render: val => formatCurrencyCell(val),
    },
  ]

  return (
    <DashboardLayout>
      <Helmet>
        <title>
          {salaryCode ? `${title}` : 'Tạo bảng lương'} | {systemName}
        </title>
      </Helmet>

      <PageTitle
        title={salaryCode ? `${title}` : 'Tạo bảng lương'}
        showTitle={true}
        location={props.location}
      />

      {!salaryCode && (
        <ContentBlockWrapper style={{ marginBottom: 20 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Tiêu đề bảng lương:</Text>
                </div>
                <Input
                  placeholder="Nhập tiêu đề trình duyệt lương"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                />
              </Col>
              <Col span={6}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Tháng lương:</Text>
                </div>
                <DatePicker
                  picker="month"
                  value={salaryMonth}
                  onChange={date => {
                    if (date) {
                      setSalaryMonth(date)
                      setTitle(
                        `Bảng thanh toán lương nhân viên tháng ${date.format(
                          'MM/YYYY'
                        )}`
                      )
                    }
                  }}
                  format="MM/YYYY"
                  style={{ width: '100%' }}
                  placeholder="Chọn tháng lương"
                />
              </Col>
            </Row>

            <Divider style={{ margin: '12px 0' }} />

            <Row gutter={16} align="middle">
              <Col span={12}>
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  style={{ display: 'none' }}
                  id="excel-upload"
                  onChange={e => {
                    handleImport(e.target.files[0])
                    e.target.value = null // Reset so re-selecting same file triggers again
                  }}
                />
                <Button
                  type="dashed"
                  icon={<UploadOutlined />}
                  onClick={() =>
                    document.getElementById('excel-upload').click()
                  }
                  style={{ cursor: 'pointer' }}>
                  Import file lương (.xlsx)
                </Button>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleCreate}
                  disabled={
                    !salaryVpStore.salaryVpDetails?.length ||
                    !title ||
                    !salaryMonth
                  }>
                  Lưu bảng lương
                </Button>
              </Col>
            </Row>
          </Space>
        </ContentBlockWrapper>
      )}

      <ContentBlockWrapper>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          {salaryVpStore.salaryVpInfo.file_name && (
            <div style={{ fontWeight: 600 }}>
              File nguồn:{' '}
              <Text code>{salaryVpStore.salaryVpInfo.file_name}</Text>
            </div>
          )}
        </div>

        <TableComponent
          dataSource={salaryVpStore.salaryVpDetails}
          columns={columns}
          rowKey={record => record.ma_nv}
          loading={loading}
          onRow={record => ({
            onClick: () => {
              setSelectedRecord(record)
              setIsModalVisible(true)
            },
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 100, hideOnSinglePage: true }}
        />
      </ContentBlockWrapper>

      <SalaryVpDetailModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        data={selectedRecord}
      />
    </DashboardLayout>
  )
}

export default inject(
  'salaryVpStore',
  'loadingAnimationStore'
)(observer(SalaryVpPage))
